import React, { useEffect } from 'react';

import SourceIcon from '@mui/icons-material/Source';
import { Box, Typography, Stack, Paper, IconButton, Radio } from '@mui/material';

import { Button } from 'shared';
import {
    formatTransaction,
    formatInventory,
    getGeneratedReasoning,
} from 'components/Inventory/utils/aiMatchingHelpers';

const CompletedUserInputCard = ({
    cardData,
    index,
    classes,
    data,
    setData,
    handleEmailSourceClick,
    skipMatch,
    handleMatchClick,
    matchLoading,
    excludeLoading,
    handleExcludeClick,
    doNotShowAgainLoading,
    handleDoNotShowAgainClick,
}) => {
    useEffect(() => {
        if (!cardData) return;

        const singleTransaction = cardData.transactions?.length === 1;
        const singleInventory = cardData.matched_inventories?.length === 1;

        let selectedInventoryId = cardData.preserved_values?.selectedInventoryId || '';
        let selectedTransactionId = cardData.preserved_values?.selectedTransactionId || '';

        let needsUpdate = false;
        let updatedValues = { ...cardData.preserved_values } || {};

        if (singleTransaction && !selectedTransactionId) {
            updatedValues.selectedTransactionId = cardData.transactions[0].id.toString();
            needsUpdate = true;
        }

        if (singleInventory && !selectedInventoryId) {
            updatedValues.selectedInventoryId = cardData.matched_inventories[0].id.toString();
            needsUpdate = true;
        }

        if (needsUpdate) {
            const updatedNeedsUserInput = [...data.needs_user_input];
            updatedNeedsUserInput[index] = {
                ...cardData,
                preserved_values: updatedValues,
            };
            setData((prev) => ({
                ...prev,
                needs_user_input: updatedNeedsUserInput,
            }));
        }
    }, [cardData, index, data, setData]);

    if (!cardData) return null;

    const reasoning = getGeneratedReasoning(cardData);

    const singleTransaction = cardData.transactions?.length === 1;
    const singleInventory = cardData.matched_inventories?.length === 1;

    const selectedInventoryId = cardData.preserved_values?.selectedInventoryId || '';
    const selectedTransactionId = cardData.preserved_values?.selectedTransactionId || '';
    return (
        <Paper key={`complete-input-${index}`} elevation={3} sx={classes.completeInputCard}>
            <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle1" fontWeight="bold">
                        Transactions
                    </Typography>
                    {cardData.email_data?.length > 0 && (
                        <IconButton onClick={() => handleEmailSourceClick(cardData)} p={0}>
                            <SourceIcon fontSize="small" />
                        </IconButton>
                    )}
                </Box>

                {cardData.transactions.map((transaction, i) => (
                    <Box
                        key={i}
                        display="flex"
                        alignItems="center"
                        mb={1}
                        sx={
                            !singleTransaction
                                ? classes.transactionText(
                                      transaction.id.toString() === selectedTransactionId,
                                  )
                                : {}
                        }
                    >
                        {!singleTransaction && (
                            <Radio
                                name="selectedTransactionId"
                                value={transaction.id.toString()}
                                checked={selectedTransactionId === transaction.id.toString()}
                                onChange={(e) => {
                                    const updated = {
                                        ...cardData.preserved_values,
                                        selectedTransactionId: e.target.value,
                                    };
                                    const updatedNeedsUserInput = [...data.needs_user_input];
                                    updatedNeedsUserInput[index] = {
                                        ...cardData,
                                        preserved_values: updated,
                                    };
                                    setData((prev) => ({
                                        ...prev,
                                        needs_user_input: updatedNeedsUserInput,
                                    }));
                                }}
                                size="small"
                                sx={{ padding: 0, marginRight: 1 }}
                            />
                        )}

                        <Typography variant="body1">{formatTransaction(transaction)}</Typography>
                    </Box>
                ))}

                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mt={3}
                    mb={1}
                >
                    <Typography variant="subtitle1" fontWeight="bold">
                        Inventories
                    </Typography>
                </Box>

                {cardData.matched_inventories.map((inventory) => {
                    const isSelected = selectedInventoryId === inventory.id.toString();

                    return (
                        <Box
                            key={inventory.id}
                            display="flex"
                            alignItems="center"
                            mb={1}
                            sx={!singleInventory ? classes.transactionText(isSelected) : {}}
                        >
                            {!singleInventory && (
                                <Radio
                                    name="selectedInventoryId"
                                    value={inventory.id.toString()}
                                    checked={isSelected}
                                    onChange={(e) => {
                                        const updated = {
                                            ...cardData.preserved_values,
                                            selectedInventoryId: e.target.value,
                                        };
                                        const updatedNeedsUserInput = [...data.needs_user_input];
                                        updatedNeedsUserInput[index] = {
                                            ...cardData,
                                            preserved_values: updated,
                                        };
                                        setData((prev) => ({
                                            ...prev,
                                            needs_user_input: updatedNeedsUserInput,
                                        }));
                                    }}
                                    size="small"
                                    sx={{ padding: 0, marginRight: 1 }}
                                />
                            )}

                            <Typography variant="body1">{formatInventory(inventory)}</Typography>
                        </Box>
                    );
                })}

                <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#666', mt: 2 }}>
                    {reasoning}
                </Typography>

                <Stack
                    direction="row"
                    spacing={2}
                    justifyContent="space-between"
                    mt={3}
                    alignItems="center"
                    width="100%"
                >
                    <Box>
                        <Button
                            sx={classes.cancelButton}
                            text="Don't Show Again"
                            loading={doNotShowAgainLoading}
                            onClick={() => {
                                handleDoNotShowAgainClick(cardData, 'userInputCard');
                            }}
                        />
                    </Box>

                    <Box display="flex" gap={2}>
                        <Button
                            sx={classes.cancelButton}
                            text="Exclude"
                            loading={excludeLoading}
                            onClick={() => {
                                handleExcludeClick(cardData, 'userInputCard');
                            }}
                        />
                        <Button
                            sx={classes.cancelButton}
                            text="Skip"
                            onClick={() => {
                                skipMatch(cardData, 'userInputCard');
                            }}
                        />

                        <Button
                            text="Match"
                            variant="contained"
                            disabled={!selectedInventoryId || !selectedTransactionId}
                            onClick={() =>
                                handleMatchClick(
                                    {
                                        selectedInventoryId,
                                        selectedTransactionId,
                                    },
                                    cardData,
                                    'userInputCard',
                                )
                            }
                            loading={matchLoading}
                        />
                    </Box>
                </Stack>
            </Box>
        </Paper>
    );
};

export default CompletedUserInputCard;
